"use client";

import { useMarketingAnalytics } from "@analytics";
import { LocaleLink } from "@i18n/routing";
import { LandingAvailabilityStatus } from "@marketing/shared/components/landing-availability-status";
import { useMarketingLandingHashNavigation } from "@marketing/shared/lib/marketing-landing-hash-navigation";
import { MARKETING_LANDING_COUNTDOWN_BADGE_SHELL_CLASS } from "@marketing/shared/lib/marketing-layout-vars";
import { getMarketingLinkPrefetch } from "@marketing/shared/lib/marketing-route-prefetch";
import { cn, DaisyCountdown, DaisyCountdownValue } from "@repo/ui";
import { useTranslations } from "next-intl";
import * as React from "react";

interface TimeParts {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}

function computeParts(targetMs: number): TimeParts {
	const diff = Math.max(0, targetMs - Date.now());
	const totalSeconds = Math.floor(diff / 1000);
	return {
		days: Math.floor(totalSeconds / 86_400),
		hours: Math.floor((totalSeconds % 86_400) / 3600),
		minutes: Math.floor((totalSeconds % 3600) / 60),
		seconds: totalSeconds % 60,
	};
}

export interface LandingLaunchCountdownBadgeProps {
	className?: string;
}

/**
 * Marketing hero: early-release status + countdown to general release (local midnight 1 Oct 2026).
 * Client-only tick — avoids SSR hydration skew for live digits.
 */
export function LandingLaunchCountdownBadge({
	className,
}: LandingLaunchCountdownBadgeProps) {
	const t = useTranslations("marketing.landing.hero");
	const { landingLaunchCountdownAvailabilityClicked } =
		useMarketingAnalytics();
	const { onHashHrefClick } = useMarketingLandingHashNavigation();
	const [parts, setParts] = React.useState<TimeParts | null>(null);

	const availabilityScrollHref = "/#earlyaccess";

	const handleAvailabilityScrollClick = (
		e: React.MouseEvent<HTMLAnchorElement>,
	) => {
		landingLaunchCountdownAvailabilityClicked({
			href: availabilityScrollHref,
		});
		onHashHrefClick(e, availabilityScrollHref);
	};

	React.useEffect(() => {
		const targetMs = new Date(2026, 9, 1, 0, 0, 0, 0).getTime();
		const tick = () => setParts(computeParts(targetMs));
		tick();
		const id = window.setInterval(tick, 1000);
		return () => clearInterval(id);
	}, []);

	const countdownDigitClassName =
		"font-black font-sans text-base leading-none text-slate-900 tabular-nums sm:text-lg dark:text-white";

	const countdownUnitLabelClassName =
		"font-medium text-[9px] text-slate-600 uppercase tracking-wide sm:text-[10px] sm:tracking-wider dark:text-neutral-400";

	const segments = parts !== null && (
		<div className="grid auto-cols-max grid-flow-col gap-2 text-center sm:gap-3">
			<div className="flex flex-col items-center gap-0.5">
				<DaisyCountdown className={countdownDigitClassName}>
					<DaisyCountdownValue
						value={parts.days}
						digits={2}
						aria-label={t("launchCountdownDaysAria", {
							count: parts.days,
						})}
					/>
				</DaisyCountdown>
				<span className={countdownUnitLabelClassName}>
					{t("launchCountdownDays")}
				</span>
			</div>
			<div className="flex flex-col items-center gap-0.5">
				<DaisyCountdown className={countdownDigitClassName}>
					<DaisyCountdownValue
						value={parts.hours}
						digits={2}
						aria-label={t("launchCountdownHoursAria", {
							count: parts.hours,
						})}
					/>
				</DaisyCountdown>
				<span className={countdownUnitLabelClassName}>
					{t("launchCountdownHours")}
				</span>
			</div>
			<div className="flex flex-col items-center gap-0.5">
				<DaisyCountdown className={countdownDigitClassName}>
					<DaisyCountdownValue
						value={parts.minutes}
						digits={2}
						aria-label={t("launchCountdownMinutesAria", {
							count: parts.minutes,
						})}
					/>
				</DaisyCountdown>
				<span className={countdownUnitLabelClassName}>
					{t("launchCountdownMinutes")}
				</span>
			</div>
			<div className="flex flex-col items-center gap-0.5">
				<DaisyCountdown className={countdownDigitClassName}>
					<DaisyCountdownValue
						value={parts.seconds}
						digits={2}
						aria-label={t("launchCountdownSecondsAria", {
							count: parts.seconds,
						})}
					/>
				</DaisyCountdown>
				<span className={countdownUnitLabelClassName}>
					{t("launchCountdownSeconds")}
				</span>
			</div>
		</div>
	);

	const headlineClassName =
		"font-black text-[10px] uppercase tracking-wider text-gray-500 sm:text-xs dark:text-neutral-400";

	return (
		<section
			className={cn(
				"mx-auto inline-grid w-max grid-cols-[auto_1px_auto] grid-rows-[auto_auto] gap-x-5 gap-y-3 rounded-none border px-6 py-5 font-sans shadow-sm backdrop-blur-sm sm:gap-x-7 sm:gap-y-3 sm:px-8 sm:py-6",
				MARKETING_LANDING_COUNTDOWN_BADGE_SHELL_CLASS,
				className,
			)}
			aria-label={t("launchCountdownRegionAria")}
		>
			<div className="col-start-1 row-start-1 flex justify-center text-center">
				<p className={headlineClassName}>
					{t("launchCountdownEarlyRelease")}
				</p>
			</div>

			<div
				className="col-start-2 row-span-2 row-start-1 w-px justify-self-center bg-slate-300/90 dark:bg-neutral-600"
				aria-hidden
			/>

			<div className="col-start-3 row-start-1 flex justify-center text-center">
				<p className={headlineClassName}>
					{t("launchCountdownGeneralRelease")}
				</p>
			</div>

			<div className="col-start-1 row-start-2 flex justify-center">
				<LocaleLink
					href={availabilityScrollHref}
					className="block cursor-pointer rounded-xl hover:no-underline focus-visible:no-underline active:no-underline"
					onClick={handleAvailabilityScrollClick}
					prefetch={getMarketingLinkPrefetch(availabilityScrollHref)}
					aria-label={t("launchCountdownAvailabilityScrollAria")}
				>
					<LandingAvailabilityStatus />
				</LocaleLink>
			</div>

			<div className="col-start-3 row-start-2 flex justify-center self-start">
				{segments ?? (
					<div
						className="grid h-14 w-max grid-cols-4 gap-2 sm:h-16 sm:gap-3"
						aria-hidden
					>
						{Array.from({ length: 4 }).map((_, i) => (
							<div
								key={`sk-${i}`}
								className="flex flex-col items-center gap-1"
							>
								<div className="h-5 w-full max-w-[2.25rem] animate-pulse rounded bg-slate-200/80 sm:h-6" />
								<div className="h-2 w-8 animate-pulse rounded bg-slate-200/60" />
							</div>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
